import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useVersionCheck } from "@/hooks/useVersionCheck";

/**
 * Stale-bundle defense, two layers (see useVersionCheck):
 *
 * 1. While the user stays on a page we never reload out from under them —
 *    a fixed gold banner nudges until they click Refresh.
 * 2. The moment they NAVIGATE anywhere with an update pending, we turn that
 *    SPA route change into a full reload. The page is changing anyway, so
 *    this is invisible to the user — and it caps how long a stale bundle can
 *    survive at "until the next click", which closes the recurring QA class
 *    of day-old tabs reporting already-fixed bugs.
 */
export function UpdateBanner() {
  const updateAvailable = useVersionCheck();
  const location = useLocation();
  const lastPath = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname === lastPath.current) return;
    lastPath.current = location.pathname;
    if (updateAvailable) window.location.reload();
  }, [location.pathname, updateAvailable]);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-[100] flex items-center justify-center gap-3 bg-gold px-4 py-3 text-sm font-medium text-navy shadow-lg"
    >
      <span>A new version of Demore is available — refresh to avoid stale-screen bugs.</span>
      <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>
        Refresh now
      </Button>
    </div>
  );
}
